
import { useState, useEffect, useRef } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { supabase } from '../../lib/supabase';
import { findQrSession, markQrSessionUsed } from '../../db/qrSessionRepository';


export default function QRScanner() {
  const [scanning, setScanning] = useState(false);
  const [error, setError] = useState('');
  const [user, setUser] = useState<any>(null);
  const [isCheckingAuth, setIsCheckingAuth] = useState(true);
  const [permissionDenied, setPermissionDenied] = useState(false);
  const navigate = useNavigate();
  const { t } = useTranslation();
  const scanIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const hasStartedRef = useRef(false);

  useEffect(() => {
    checkUser();

    return () => {
      if (scanIntervalRef.current) {
        clearInterval(scanIntervalRef.current);
      }
      if (streamRef.current) {
        streamRef.current.getTracks().forEach(track => track.stop());
      }
    };
  }, []);

  // 사용자 확인 후 바로 스캔 시작
  useEffect(() => {
    if (!isCheckingAuth && user && !hasStartedRef.current) {
      hasStartedRef.current = true;
      startScanning();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isCheckingAuth, user]);

  const checkUser = async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      setUser(user);
    } catch (err) {
      console.error('사용자 확인 오류:', err);
    } finally {
      setIsCheckingAuth(false);
    }
  };

  const startScanning = async () => {
    setScanning(true);
    setError('');
    setPermissionDenied(false);

    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: 'environment' }
      });

      streamRef.current = stream;

      const video = document.getElementById('qr-video') as HTMLVideoElement;
      if (video) {
        video.srcObject = stream;
        video.play();
        detectQRCode(video, stream);
      }
    } catch (err: any) {
      if (err.name === 'NotAllowedError' || err.name === 'PermissionDeniedError') {
        setPermissionDenied(true);
      } else {
        setError(t('qr.camera_unavailable'));
      }
      setScanning(false);
    }
  };

  const detectQRCode = async (video: HTMLVideoElement, stream: MediaStream) => {
    const canvas = document.createElement('canvas');
    const context = canvas.getContext('2d');

    scanIntervalRef.current = setInterval(async () => {
      if (video.readyState === video.HAVE_ENOUGH_DATA) {
        canvas.height = video.videoHeight;
        canvas.width = video.videoWidth;
        context?.drawImage(video, 0, 0, canvas.width, canvas.height);

        const imageData = context?.getImageData(0, 0, canvas.width, canvas.height);

        if (imageData) {
          try {
            const code = await detectQRFromImageData(imageData);
            if (code) {
              if (scanIntervalRef.current) {
                clearInterval(scanIntervalRef.current);
              }
              stream.getTracks().forEach(track => track.stop());
              await handleQRCode(code);
            }
          } catch {
            // QR 코드를 찾지 못함, 계속 스캔
          }
        }
      }
    }, 500);

    setTimeout(() => {
      if (scanIntervalRef.current) {
        clearInterval(scanIntervalRef.current);
      }
      stream.getTracks().forEach(track => track.stop());
      if (scanning) {
        setError(t('qr.qr_not_found_timeout'));
        setScanning(false);
      }
    }, 30000);
  };

  const detectQRFromImageData = async (imageData: ImageData): Promise<string | null> => {
    return new Promise((resolve) => {
      const worker = new Worker(
        URL.createObjectURL(
          new Blob([`
            importScripts('https://cdn.jsdelivr.net/npm/jsqr@1.4.0/dist/jsQR.min.js');
            self.onmessage = function(e) {
              const code = jsQR(e.data.data, e.data.width, e.data.height);
              self.postMessage(code ? code.data : null);
            };
          `], { type: 'application/javascript' })
        )
      );

      worker.onmessage = (e) => {
        worker.terminate();
        resolve(e.data);
      };

      worker.postMessage(imageData);
    });
  };

  const handleQRCode = async (qrData: string) => {
    try {
      let token: string | null = null;

      // URL 형태인지 확인
      if (qrData.startsWith('http://') || qrData.startsWith('https://')) {
        const url = new URL(qrData);
        token = url.searchParams.get('token');
      } else {
        // 기존 JSON 형태 호환
        try {
          const data = JSON.parse(qrData);
          token = data.token;
        } catch {
          token = null;
        }
      }

      if (!token) {
        setError(t('qr.invalid_qr'));
        setScanning(false);
        return;
      }

      if (!user) {
        setError(t('qr.login_first'));
        setScanning(false);
        return;
      }

      const session = await findQrSession(token);

      if (!session) {
        setError(t('qr.qr_not_found'));
        setScanning(false);
        return;
      }

      if (session.is_used) {
        setError(t('qr.qr_already_used'));
        setScanning(false);
        return;
      }

      const expiresAt = new Date(session.expires_at);
      if (expiresAt < new Date()) {
        setError(t('qr.qr_expired'));
        setScanning(false);
        return;
      }

      await markQrSessionUsed(token, user.id);

      // QR 세션 정보 저장 (로그아웃 감지용)
      localStorage.setItem('qr_session_token', token);
      localStorage.setItem('qr_user_id', user.id);

      setScanning(false);

      // 프레젠테이션 선택 페이지로 이동
      navigate('/qr-select');

    } catch {
      setError(t('qr.qr_scan_error'));
      setScanning(false);
    }
  };

  const handleRetry = () => {
    setError('');
    setPermissionDenied(false);
    hasStartedRef.current = false;
    startScanning();
  };


  // 인증 확인 중 로딩
  if (isCheckingAuth) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-violet-50 to-purple-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-4 border-violet-500 border-t-transparent"></div>
      </div>
    );
  }

  // 로그인 안 되어 있으면 로그인 유도 화면 표시
  if (!user) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-violet-50 to-purple-50">
        <div className="max-w-2xl mx-auto px-4 py-8">
          <div className="bg-white rounded-2xl shadow-xl overflow-hidden">
            <div className="bg-gradient-to-r from-violet-500 to-purple-500 px-6 py-8 text-white">
              <div className="flex items-center justify-between">
                <div>
                  <h1 className="text-3xl font-bold mb-2">{t('qr.scan_title')}</h1>
                  <p className="text-violet-100">{t('qr.scan_subtitle')}</p>
                </div>
                <div className="w-16 h-16 bg-white/20 rounded-2xl flex items-center justify-center">
                  <i className="ri-qr-scan-2-line text-4xl"></i>
                </div>
              </div>
            </div>

            <div className="p-6">
              <div className="bg-amber-50 border border-amber-200 text-amber-700 px-4 py-3 rounded-lg mb-6 flex items-start gap-3">
                <i className="ri-information-line text-xl flex-shrink-0 mt-0.5"></i>
                <span className="text-sm">{t('qr.login_required_scan')}</span>
              </div>

              <div className="space-y-3">
                <Link
                  to="/login"
                  className="w-full bg-gradient-to-r from-violet-500 to-purple-500 text-white py-4 rounded-lg font-medium hover:from-violet-600 hover:to-purple-600 transition-all flex items-center justify-center gap-2 whitespace-nowrap"
                >
                  <i className="ri-login-box-line text-xl"></i>
                  {t('qr.login_action')}
                </Link>

                <Link
                  to="/signup"
                  className="w-full bg-gray-100 text-gray-700 py-3 rounded-lg font-medium hover:bg-gray-200 transition-all flex items-center justify-center gap-2 whitespace-nowrap"
                >
                  <i className="ri-user-add-line"></i>
                  {t('common.signup')}
                </Link>

                <Link
                  to="/"
                  className="w-full bg-white border-2 border-gray-200 text-gray-700 py-3 rounded-lg font-medium hover:bg-gray-50 transition-all flex items-center justify-center gap-2 whitespace-nowrap"
                >
                  <i className="ri-arrow-left-line"></i>
                  {t('qr.go_home_action')}
                </Link>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // 카메라 권한 거부 화면
  if (permissionDenied) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-violet-50 to-purple-50">
        <div className="max-w-2xl mx-auto px-4 py-8">
          <div className="bg-white rounded-2xl shadow-xl overflow-hidden">
            <div className="bg-gradient-to-r from-violet-500 to-purple-500 px-6 py-8 text-white">
              <div className="flex items-center justify-between">
                <div>
                  <h1 className="text-3xl font-bold mb-2">{t('qr.scan_title')}</h1>
                  <p className="text-violet-100">{t('qr.scan_subtitle')}</p>
                </div>
                <div className="w-16 h-16 bg-white/20 rounded-2xl flex items-center justify-center">
                  <i className="ri-qr-scan-2-line text-4xl"></i>
                </div>
              </div>
            </div>

            <div className="p-6">
              <div className="text-center py-6">
                <div className="w-24 h-24 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-6">
                  <i className="ri-camera-off-line text-5xl text-red-500"></i>
                </div>
                <h2 className="text-xl font-bold text-gray-800 mb-3">{t('qr.camera_permission_required')}</h2>
                <p className="text-gray-600 mb-6">{t('qr.camera_permission_desc')}</p>

                <div className="bg-violet-50 border border-violet-200 rounded-xl p-4 mb-6 text-left">
                  <p className="text-sm font-medium text-violet-800 mb-3 flex items-center gap-2">
                    <i className="ri-settings-3-line"></i>
                    {t('qr.permission_settings')}
                  </p>
                  <div className="space-y-3 text-sm text-violet-700">
                    <div className="flex items-start gap-2">
                      <span className="bg-violet-200 text-violet-800 w-5 h-5 rounded-full flex items-center justify-center flex-shrink-0 text-xs font-bold">1</span>
                      <span>브라우저 주소창 왼쪽의 <strong>자물쇠 아이콘</strong> 또는 <strong>설정 아이콘</strong>을 탭하세요</span>
                    </div>
                    <div className="flex items-start gap-2">
                      <span className="bg-violet-200 text-violet-800 w-5 h-5 rounded-full flex items-center justify-center flex-shrink-0 text-xs font-bold">2</span>
                      <span><strong>사이트 설정</strong> 또는 <strong>권한</strong>을 선택하세요</span>
                    </div>
                    <div className="flex items-start gap-2">
                      <span className="bg-violet-200 text-violet-800 w-5 h-5 rounded-full flex items-center justify-center flex-shrink-0 text-xs font-bold">3</span>
                      <span><strong>카메라</strong> 항목을 찾아 <strong>허용</strong>으로 변경하세요</span>
                    </div>
                    <div className="flex items-start gap-2">
                      <span className="bg-violet-200 text-violet-800 w-5 h-5 rounded-full flex items-center justify-center flex-shrink-0 text-xs font-bold">4</span>
                      <span>설정 후 아래 <strong>다시 시도</strong> 버튼을 눌러주세요</span>
                    </div>
                  </div>
                </div>

                <div className="bg-gray-50 rounded-xl p-4 mb-6">
                  <p className="text-xs text-gray-500 mb-2">{t('qr.ios_safari_user')}</p>
                  <p className="text-sm text-gray-600">{t('qr.ios_safari_hint')}</p>
                </div>

                <div className="space-y-3">
                  <button
                    onClick={handleRetry}
                    className="w-full bg-gradient-to-r from-violet-500 to-purple-500 text-white py-4 rounded-lg font-medium hover:from-violet-600 hover:to-purple-600 transition-all flex items-center justify-center gap-2 whitespace-nowrap cursor-pointer"
                  >
                    <i className="ri-refresh-line text-xl"></i>
                    {t('qr.retry_action')}
                  </button>

                  <Link
                    to="/dashboard"
                    className="w-full bg-white border-2 border-gray-200 text-gray-700 py-3 rounded-lg font-medium hover:bg-gray-50 transition-all flex items-center justify-center gap-2 whitespace-nowrap"
                  >
                    <i className="ri-arrow-left-line"></i>
                    {t('qr.back_to_dashboard')}
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }



  return (
    <div className="min-h-screen bg-gradient-to-br from-violet-50 to-purple-50">
      <div className="max-w-2xl mx-auto px-4 py-8">
        <div className="bg-white rounded-2xl shadow-xl overflow-hidden">
          <div className="bg-gradient-to-r from-violet-500 to-purple-500 px-6 py-8 text-white">
            <div className="flex items-center justify-between">
              <div>
                <h1 className="text-3xl font-bold mb-2">{t('qr.scan_title')}</h1>
                <p className="text-violet-100">{t('qr.scan_subtitle')}</p>
              </div>
              <div className="w-16 h-16 bg-white/20 rounded-2xl flex items-center justify-center">
                <i className="ri-qr-scan-2-line text-4xl"></i>
              </div>
            </div>
          </div>

          <div className="p-6">
            {error && (
              <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg mb-6 flex items-start gap-3">
                <i className="ri-error-warning-line text-xl flex-shrink-0 mt-0.5"></i>
                <span className="text-sm">{error}</span>
              </div>
            )}

            <div className="bg-gray-900 rounded-xl overflow-hidden mb-6 relative" style={{ height: '400px' }}>
              {scanning ? (
                <>
                  <video
                    id="qr-video"
                    className="w-full h-full object-cover"
                    playsInline
                  />
                  {/* 스캔 가이드 오버레이 */}
                  <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                    <div className="w-64 h-64 border-2 border-white/50 rounded-2xl relative">
                      <div className="absolute top-0 left-0 w-8 h-8 border-t-4 border-l-4 border-violet-400 rounded-tl-lg"></div>
                      <div className="absolute top-0 right-0 w-8 h-8 border-t-4 border-r-4 border-violet-400 rounded-tr-lg"></div>
                      <div className="absolute bottom-0 left-0 w-8 h-8 border-b-4 border-l-4 border-violet-400 rounded-bl-lg"></div>
                      <div className="absolute bottom-0 right-0 w-8 h-8 border-b-4 border-r-4 border-violet-400 rounded-br-lg"></div>
                    </div>
                  </div>
                  <div className="absolute bottom-4 left-0 right-0 text-center">
                    <span className="bg-black/50 text-white px-4 py-2 rounded-full text-sm">
                      {t('qr.align_qr')}
                    </span>
                  </div>
                </>
              ) : (
                <div className="w-full h-full flex items-center justify-center">
                  <div className="text-center">
                    <div className="animate-spin rounded-full h-10 w-10 border-2 border-violet-400 border-t-transparent mx-auto mb-4"></div>
                    <p className="text-gray-400">{t('qr.camera_preparing')}</p>
                  </div>
                </div>
              )}
            </div>

            <div className="space-y-3">
              {!scanning && (
                <button
                  onClick={handleRetry}
                  className="w-full bg-gradient-to-r from-violet-500 to-purple-500 text-white py-4 rounded-lg font-medium hover:from-violet-600 hover:to-purple-600 transition-all flex items-center justify-center gap-2 whitespace-nowrap cursor-pointer"
                >
                  <i className="ri-refresh-line text-xl"></i>
                  {t('qr.scan_again')}
                </button>
              )}

              {scanning && (
                <div className="w-full bg-violet-100 text-violet-700 py-4 rounded-lg font-medium flex items-center justify-center gap-2">
                  <div className="animate-spin rounded-full h-5 w-5 border-2 border-violet-600 border-t-transparent"></div>
                  {t('qr.scanning')}
                </div>
              )}

              <Link
                to="/dashboard"
                className="w-full bg-white border-2 border-gray-200 text-gray-700 py-3 rounded-lg font-medium hover:bg-gray-50 transition-all flex items-center justify-center gap-2 whitespace-nowrap"
              >
                <i className="ri-arrow-left-line"></i>
                {t('qr.back_to_dashboard')}
              </Link>
            </div>

            <div className="mt-6 bg-violet-50 border border-violet-200 rounded-xl p-4">
              <div className="flex gap-3">
                <i className="ri-lightbulb-line text-xl text-violet-600 flex-shrink-0 mt-0.5"></i>
                <div className="text-sm text-violet-800">
                  <p className="font-medium mb-1">{t('qr.tip')}</p>
                  <p className="text-violet-700">{t('qr.tip_desc')}</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
